import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db, callFunction } from '../firebase';
import { useCompanySession } from '../session';
import CompanyLayout from '../components/CompanyLayout';
import { useI18n } from '../i18n';
import {
    canStaffFileClaims,
    claimStatusClass,
    fileToPhotoPayload,
    normalizeClaimStatus,
    nextClaimStatuses,
    uploadClaimPhotos
} from '../claims';
import { formatGmdAmount } from '../wallets';
import { moduleOn, useTenantFeatures } from '../tenantFeatures';

const emptyForm = {
    policyId: '',
    incidentDate: '',
    incidentLocation: '',
    incidentDescription: '',
    estimatedRepairCost: ''
};

function Claims() {
    const { session, loading } = useCompanySession();
    const { t } = useI18n();
    const features = useTenantFeatures(session && session.tenantId);
    const [claims, setClaims] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [ready, setReady] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [queryText, setQueryText] = useState('');
    const [openId, setOpenId] = useState('');

    const canFile = session && canStaffFileClaims(session.role);

    const load = async (tenantId) => {
        const [cSnap, pSnap] = await Promise.all([
            getDocs(collection(db, 'tenants', tenantId, 'claims')),
            getDocs(collection(db, 'tenants', tenantId, 'policies'))
        ]);
        const cl = [];
        cSnap.forEach((d) => cl.push({ id: d.id, ...d.data() }));
        cl.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
        const pl = [];
        pSnap.forEach((d) => pl.push({ id: d.id, ...d.data() }));
        setClaims(cl);
        setPolicies(pl);
        setReady(true);
    };

    useEffect(() => {
        if (session?.tenantId) load(session.tenantId).catch(() => setReady(true));
    }, [session]);

    const filteredPolicies = useMemo(() => {
        const q = queryText.trim().toLowerCase();
        if (!q) return policies.slice(0, 80);
        return policies.filter((p) => {
            const holder = (p.policyHolder && p.policyHolder.fullName) || '';
            const plate = (p.vehicle && p.vehicle.registrationNumber) || '';
            return [p.policyNumber, holder, plate, p.id].join(' ').toLowerCase().includes(q);
        }).slice(0, 80);
    }, [policies, queryText]);

    const selectedPolicy = policies.find((p) => p.id === form.policyId);

    const submitClaim = async (e) => {
        e.preventDefault();
        if (!canFile) return;
        setBusy(true);
        setMessage('');
        try {
            let photos = [];
            if (files.length) {
                try {
                    photos = await Promise.all(Array.from(files).slice(0, 4).map(fileToPhotoPayload));
                } catch (err) {
                    setMessage(err.message || t('claims.photoSkip'));
                }
            }
            const row = await callFunction('fileClaim', {
                policyId: form.policyId,
                incidentDate: form.incidentDate,
                incidentLocation: form.incidentLocation,
                incidentDescription: form.incidentDescription,
                estimatedRepairCost: Number(form.estimatedRepairCost) || 0,
                photos: photos.filter(Boolean)
            });
            if (files.length && row && row.id) {
                const up = await uploadClaimPhotos(session.tenantId, row.id, files);
                if (up.skipped) {
                    setMessage(t('claims.filed') + ' ' + (row.claimNumber || '') + '. ' + t('claims.photoSkip'));
                } else if (up.urls.length) {
                    await updateDoc(doc(db, 'tenants', session.tenantId, 'claims', row.id), {
                        photoUrls: [...(row.photoUrls || []), ...up.urls],
                        photosSkipped: false
                    });
                    setMessage(t('claims.filed') + ' ' + (row.claimNumber || ''));
                } else {
                    setMessage(t('claims.filed') + ' ' + (row.claimNumber || ''));
                }
            } else {
                setMessage(t('claims.filed') + ' ' + ((row && row.claimNumber) || ''));
            }
            setForm(emptyForm);
            setFiles([]);
            setShowForm(false);
            await load(session.tenantId);
        } catch (err) {
            setMessage(err.message || t('claims.fileFailed'));
        }
        setBusy(false);
    };

    const setStatus = async (claim, status) => {
        if (!canFile) return;
        setBusy(true);
        setMessage('');
        try {
            const now = new Date().toISOString();
            const history = Array.isArray(claim.statusHistory) ? claim.statusHistory.slice() : [];
            history.push({ status, at: now, by: session.email });
            await updateDoc(doc(db, 'tenants', session.tenantId, 'claims', claim.id), {
                status,
                updatedAt: now,
                statusHistory: history,
                settledAmount: status === 'paid' ? Number(claim.estimatedRepairCost || claim.reserveAmount || 0) : (claim.settledAmount || 0)
            });
            await load(session.tenantId);
        } catch (err) {
            setMessage(err.message || t('claims.updateFailed'));
        }
        setBusy(false);
    };

    if (loading || !session || !ready) return <div className="p-5">{t('claims.loading')}</div>;
    if (!moduleOn(features, 'claimsFnol')) {
        return (
            <CompanyLayout session={session}>
                <div className="alert alert-warning">{t('modules.disabled')}</div>
            </CompanyLayout>
        );
    }

    return (
        <CompanyLayout session={session}>
            <div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
                <p className="text-muted mb-0">{t('claims.intro')}</p>
                {canFile && (
                    <button type="button" className="bd-btn bd-btn-warning" onClick={() => setShowForm((v) => !v)}>
                        {showForm ? t('common.cancel') : t('claims.new')}
                    </button>
                )}
            </div>
            {message && <div className="alert alert-info">{message}</div>}

            {showForm && canFile && (
                <form className="bd-card mb-3" onSubmit={submitClaim}>
                    <h5 className="mb-3">{t('claims.new')}</h5>
                    <div className="mb-3">
                        <label className="form-label">{t('claims.policySearch')}</label>
                        <input className="form-control mb-2" value={queryText} onChange={(e) => setQueryText(e.target.value)} placeholder={t('claims.policySearchPh')} />
                        <select className="form-select" required value={form.policyId} onChange={(e) => setForm({ ...form, policyId: e.target.value })}>
                            <option value="">{t('claims.pickPolicy')}</option>
                            {filteredPolicies.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {(p.policyNumber || p.id) + ' · ' + ((p.policyHolder && p.policyHolder.fullName) || '') + (p.vehicle && p.vehicle.registrationNumber ? ' · ' + p.vehicle.registrationNumber : '')}
                                </option>
                            ))}
                        </select>
                        {selectedPolicy && (
                            <div className="small text-muted mt-1">
                                {(selectedPolicy.policyHolder && selectedPolicy.policyHolder.fullName) || ''} · {selectedPolicy.policyType || ''} · {selectedPolicy.status}
                            </div>
                        )}
                    </div>
                    <div className="row g-2">
                        <div className="col-md-4">
                            <label className="form-label">{t('claims.incidentDate')}</label>
                            <input type="date" className="form-control" required value={form.incidentDate} onChange={(e) => setForm({ ...form, incidentDate: e.target.value })} />
                        </div>
                        <div className="col-md-8">
                            <label className="form-label">{t('claims.place')}</label>
                            <input className="form-control" value={form.incidentLocation} onChange={(e) => setForm({ ...form, incidentLocation: e.target.value })} placeholder={t('claims.placePh')} />
                        </div>
                        <div className="col-12">
                            <label className="form-label">{t('claims.description')}</label>
                            <textarea className="form-control" rows={3} required value={form.incidentDescription} onChange={(e) => setForm({ ...form, incidentDescription: e.target.value })} />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">{t('claims.estimate')}</label>
                            <input type="number" min="0" className="form-control" value={form.estimatedRepairCost} onChange={(e) => setForm({ ...form, estimatedRepairCost: e.target.value })} />
                        </div>
                        <div className="col-md-8">
                            <label className="form-label">{t('claims.photos')}</label>
                            <input type="file" accept="image/*" multiple className="form-control" onChange={(e) => setFiles(e.target.files || [])} />
                            <div className="small text-muted">{t('claims.photosHint')}</div>
                        </div>
                    </div>
                    <button type="submit" className="bd-btn bd-btn-warning mt-3" disabled={busy}>{busy ? t('common.loading') : t('claims.file')}</button>
                </form>
            )}

            <div className="bd-card">
                <div style={{ overflowX: 'auto' }}>
                    <table className="bd-table">
                        <thead>
                            <tr>
                                <th>{t('claims.id')}</th>
                                <th>{t('claims.insured')}</th>
                                <th>{t('claims.line')}</th>
                                <th>{t('claims.reserve')}</th>
                                <th>{t('claims.status')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {claims.map((c) => {
                                const status = normalizeClaimStatus(c.status);
                                const next = nextClaimStatuses(status);
                                const open = openId === c.id;
                                return (
                                    <React.Fragment key={c.id}>
                                        <tr className="bd-row-click" onClick={() => setOpenId(open ? '' : c.id)}>
                                            <td>{c.claimNumber || c.id}</td>
                                            <td>{c.clientName || c.fullName}</td>
                                            <td>{c.policyType || c.policyNumber}</td>
                                            <td>{formatGmdAmount(c.estimatedRepairCost || c.reserveAmount || 0)}</td>
                                            <td><span className={claimStatusClass(status)}>{t('claims.st.' + status)}</span></td>
                                        </tr>
                                        {open && (
                                            <tr className="bd-row-detail">
                                                <td colSpan={5}>
                                                    <div className="small mb-2">{c.incidentDescription}</div>
                                                    <div className="small text-muted mb-2">
                                                        {(c.incidentDate || '') + (c.incidentLocation ? ' · ' + c.incidentLocation : '')}
                                                        {c.source === 'fnol' ? ' · FNOL' : ''}
                                                        {c.photosSkipped ? ' · ' + t('claims.photoSkip') : ''}
                                                    </div>
                                                    {(c.photoUrls || []).length > 0 && (
                                                        <div className="d-flex gap-2 flex-wrap mb-2">
                                                            {c.photoUrls.map((url, i) => (
                                                                <a key={i} href={url} target="_blank" rel="noreferrer">
                                                                    <img src={url} alt="" style={{ height: 64, borderRadius: 6, objectFit: 'cover' }} />
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {canFile && next.length > 0 && (
                                                        <div className="d-flex gap-2 flex-wrap">
                                                            {next.map((st) => (
                                                                <button key={st} type="button" className="bd-btn bd-btn-secondary bd-btn-sm" disabled={busy} onClick={() => setStatus(c, st)}>
                                                                    {t('claims.move')} {t('claims.st.' + st)}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {claims.length === 0 && (
                                <tr><td colSpan={5} className="text-muted">{t('claims.none')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </CompanyLayout>
    );
}

export default Claims;
